<template>
  <v-container fluid>

    <!-- 결제 상세 정보 팝업 -->
    <v-dialog persistent overlay-color="#00000040" v-model="dialog_detail" @keydown.esc="dialog_detail = false" max-width="600px">
      <v-card>
        <!-- 헤더 -->
        <v-card-title class="pa-4">
          <span style="font-size:20px;font-weight:600;">결제내역 상세</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="dialog_detail = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <!-- 컨텐츠 -->
        <v-card-text class="pa-4" style="max-height:500px;overflow-y:auto;">
          <!-- 고객정보 -->
          <div class="section-title">고객정보</div>
          <v-simple-table dense class="info-table mb-4">
            <tbody>
              <tr><td class="label-cell">이름</td><td class="value-cell">{{ detailData.buyer_name || '-' }}</td></tr>
              <tr><td class="label-cell" style="color:#e53935;">연락처</td><td class="value-cell">{{ detailData.buyer_tel || '-' }}</td></tr>
              <tr><td class="label-cell">이메일</td><td class="value-cell">{{ detailData.buyer_email || '-' }}</td></tr>
              <tr><td class="label-cell">주소</td><td class="value-cell">{{ detailData.buyer_addr || '-' }}</td></tr>
            </tbody>
          </v-simple-table>

          <!-- 결제정보 -->
          <v-row dense align="center" class="mb-2">
            <v-col><span class="section-title">결제정보</span></v-col>
            <v-col cols="auto">
              <span style="color:#888;font-size:12px;">{{ detailData.merchant_uid }}</span>
              <v-btn icon x-small @click="copyToClipboard(detailData.merchant_uid)"><v-icon small>mdi-content-copy</v-icon></v-btn>
            </v-col>
          </v-row>

          <v-card outlined class="pa-3 mb-3">
            <v-row dense align="center" class="mb-2">
              <v-col>
                <div style="font-weight:500;">{{ detailData.name || '-' }}</div>
                <div style="color:#888;font-size:12px;">{{ formatDate(detailData.paid_at || detailData.started_at) }}</div>
              </v-col>
              <v-col cols="auto">
                <v-btn x-small outlined class="mr-1" :disabled="!detailData.receipt_url" @click="openReceipt(detailData)">승인 영수증</v-btn>
                <v-btn x-small outlined :disabled="!detailData.cancel_receipt_urls || detailData.cancel_receipt_urls.length == 0" @click="openCancelReceipt">취소 영수증</v-btn>
              </v-col>
            </v-row>

            <v-simple-table dense class="info-table">
              <tbody>
                <tr><td class="label-cell">결제상태</td><td class="value-cell"><span :style="{ color: getStatusTextColor(detailData.status) }">{{ getStatusName(detailData.status) }}</span></td></tr>
                <tr v-if="detailData.cancel_reason"><td class="label-cell" style="color:#e53935;">취소 사유</td><td class="value-cell">{{ detailData.cancel_reason }}</td></tr>
                <tr><td class="label-cell">포트원 거래번호</td><td class="value-cell">{{ detailData.imp_uid }} <v-btn icon x-small @click="copyToClipboard(detailData.imp_uid)"><v-icon small>mdi-content-copy</v-icon></v-btn></td></tr>
                <tr><td class="label-cell">결제수단</td><td class="value-cell">{{ getPayMethodName(detailData.pay_method) }} <span v-if="detailData.card_name" style="color:#888;">{{ detailData.card_name }} {{ detailData.card_number }}</span></td></tr>
                <tr><td class="label-cell">결제금액</td><td class="value-cell">{{ priceToString(detailData.amount) }} {{ getCurrencyCode(detailData.currency) }}</td></tr>
                <tr><td class="label-cell">주문금액</td><td class="value-cell">{{ priceToString(detailData.amount) }} {{ getCurrencyCode(detailData.currency) }}</td></tr>
                <tr v-if="detailData.cancel_amount > 0"><td class="label-cell">취소금액</td><td class="value-cell" style="color:#e53935;">-{{ priceToString(detailData.cancel_amount) }} {{ getCurrencyCode(detailData.currency) }}</td></tr>
                <tr><td class="label-cell">결제대행사</td><td class="value-cell">{{ getPgName(detailData.pg_provider) }}</td></tr>
                <tr><td class="label-cell">PG 승인번호</td><td class="value-cell">{{ detailData.pg_tid || '-' }}</td></tr>
                <tr><td class="label-cell">카드사 승인번호</td><td class="value-cell">{{ detailData.apply_num || '-' }}</td></tr>
                <tr><td class="label-cell">PG 상점아이디</td><td class="value-cell">{{ detailData.pg_id || '-' }}</td></tr>
                <tr><td class="label-cell">결제환경</td><td class="value-cell">{{ detailData.channel || '-' }}</td></tr>
                <tr><td class="label-cell">시작시간</td><td class="value-cell">{{ formatDate(detailData.started_at) }}</td></tr>
                <tr><td class="label-cell">완료시간</td><td class="value-cell">{{ formatDate(detailData.paid_at) }}</td></tr>
                <tr v-if="detailData.failed_at"><td class="label-cell">실패시간</td><td class="value-cell">{{ formatDate(detailData.failed_at) }}</td></tr>
                <tr v-if="detailData.fail_reason"><td class="label-cell">실패사유</td><td class="value-cell" style="color:#e53935;">{{ detailData.fail_reason }}</td></tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <!-- 취소내역 (cancel_history) -->
          <div v-if="detailData.cancel_history && detailData.cancel_history.length > 0">
            <div class="section-title">취소내역</div>
            <v-card outlined class="pa-3 mb-2" v-for="(cancel, idx) in detailData.cancel_history" :key="idx">
              <v-simple-table dense class="info-table">
                <tbody>
                  <tr><td class="label-cell">취소번호</td><td class="value-cell">{{ cancel.pg_tid || '-' }}</td></tr>
                  <tr><td class="label-cell">취소금액</td><td class="value-cell" style="color:#e53935;">-{{ priceToString(cancel.amount) }} {{ getCurrencyCode(detailData.currency) }}</td></tr>
                  <tr><td class="label-cell">취소사유</td><td class="value-cell">{{ cancel.reason || '-' }}</td></tr>
                  <tr><td class="label-cell">취소일시</td><td class="value-cell">{{ formatDate(cancel.cancelled_at) }}</td></tr>
                  <tr v-if="cancel.receipt_url"><td class="label-cell">영수증</td><td class="value-cell"><v-btn x-small text color="primary" @click="openCancelReceiptUrl(cancel.receipt_url)">보기</v-btn></td></tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </div>

          <!-- 카드정보 -->
          <div v-if="detailData.card_name">
            <div class="section-title">카드정보</div>
            <v-card outlined class="pa-3 mb-3">
              <v-simple-table dense class="info-table">
                <tbody>
                  <tr><td class="label-cell">카드사</td><td class="value-cell">{{ detailData.card_name }}</td></tr>
                  <tr><td class="label-cell">카드번호</td><td class="value-cell">{{ detailData.card_number || '-' }}</td></tr>
                  <tr><td class="label-cell">카드타입</td><td class="value-cell">{{ detailData.card_type == 0 ? '신용카드' : detailData.card_type == 1 ? '체크카드' : '-' }}</td></tr>
                  <tr><td class="label-cell">할부개월</td><td class="value-cell">{{ detailData.card_quota == 0 ? '일시불' : detailData.card_quota + '개월' }}</td></tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </div>

          <!-- 가상계좌 정보 -->
          <div v-if="detailData.vbank_name">
            <div class="section-title">가상계좌 정보</div>
            <v-card outlined class="pa-3 mb-3">
              <v-simple-table dense class="info-table">
                <tbody>
                  <tr><td class="label-cell">은행</td><td class="value-cell">{{ detailData.vbank_name }}</td></tr>
                  <tr><td class="label-cell">계좌번호</td><td class="value-cell">{{ detailData.vbank_num || '-' }}</td></tr>
                  <tr><td class="label-cell">예금주</td><td class="value-cell">{{ detailData.vbank_holder || '-' }}</td></tr>
                  <tr><td class="label-cell">입금기한</td><td class="value-cell">{{ formatDate(detailData.vbank_date) }}</td></tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </div>

          <!-- Custom Data -->
          <div v-if="detailData.custom_data">
            <div class="section-title">커스텀 데이터</div>
            <v-card outlined class="pa-3 mb-3">
              <pre style="font-size:12px;white-space:pre-wrap;word-break:break-all;">{{ detailData.custom_data }}</pre>
            </v-card>
          </div>
        </v-card-text>

        <!-- 닫기 버튼 -->
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn depressed @click="dialog_detail = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 검색 조건 -->
    <v-row dense align="center" class="mt-2">
      <v-col cols="12" md="2">
        <label class="menunamestyle ml-10">PG 결제내역</label>
      </v-col>
      <v-spacer/>
      <template v-if="grade >= 50">
        <v-col cols="12" md="2">
          <comdialog :comname="SEARCH_COM_NAME" @cancelcallback="comsearchclear" @callback="comsearchcb"></comdialog>
        </v-col>
      </template>
      <v-col cols="12" md="2">
        <eventdialog :eventname="SEARCH_EVENT_NAME" :comidx="SEARCH_COM_IDX" @callback="eventsearchcb"></eventdialog>
      </v-col>
      <v-col cols="12" md="2">
        <v-menu
          v-model="date1"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="search_start_date"
              :label="$t('common_search_start')"
              prepend-icon="event"
              readonly outlined dense hide-details
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="search_start_date" @input="date1 = false" 
            :first-day-of-week="0" :locale="$i18n.locale">
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="2">
        <v-menu
          v-model="date2"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="search_end_date"
              :label="$t('common_search_end')"
              prepend-icon="event"
              readonly outlined dense hide-details
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="search_end_date" @input="date2 = false" :first-day-of-week="0" :locale="$i18n.locale"></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <!-- 검색 조건 2행 -->
    <v-row dense align="center" class="mt-0">
      <v-spacer/>
      <v-col cols="12" md="1">
        <v-select
          dense v-model="search_status"
          :items="statusList"
          item-text="name"
          item-value="value"
          label="상태"
          outlined hide-details
          @change="search_run"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="search_keyword"
          label="주문자명"
          outlined dense hide-details
          clearable
          @keyup.enter="search_run"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="1">
        <v-btn class="search_btn" elevation="0" outlined large @click="search_run">조회</v-btn>
      </v-col>
    </v-row>

    <!-- 현재페이지 개수 -->
    <v-row no-gutters dense align="center" class="mt-2">
      <v-col cols="12" class="pa-0" align="start">
        <label class="total_label">현재페이지</label>
        <label class="total_label">{{ itemList.length }}건</label>
      </v-col>
    </v-row>

    <!-- 데이터 테이블 -->
    <v-row align="start" class="mt-2">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="itemList"
          no-data-text="데이터가 없습니다."
          hide-default-footer
          class="datatablestyle"
          :items-per-page="-1"
          item-key="imp_uid"
          :loading="loading"
          mobile-breakpoint="0"
          height="620px"
          fixed-header
        >
          <!-- 데이터 셀 -->
          <template v-slot:item.detail="{ item }">
            <v-icon small @click="showDetail(item)">mdi-eye</v-icon>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="getStatusColor(item.status)" dark>{{ getStatusName(item.status) }}</v-chip>
          </template>
          <template v-slot:item.paid_at="{ item }">
            <label>{{ formatDate(getStatusTime(item)) }}</label>
          </template>
          <template v-slot:item.pg_provider="{ item }">
            <label>{{ getPgName(item.pg_provider) }}</label>
          </template>
          <template v-slot:item.pay_method="{ item }">
            <label>{{ getPayMethodName(item.pay_method) }}</label>
          </template>
          <template v-slot:item.amount="{ item }">
            <label>{{ priceToString(item.amount) }} {{ getCurrencyCode(item.currency) }}</label>
          </template>
          <template v-slot:item.cancel_amount="{ item }">
            <label>{{ priceToString(item.cancel_amount || 0) }} {{ getCurrencyCode(item.currency) }}</label>
          </template>
          <template v-slot:item.cancelled_amount="{ item }">
            <label>{{ priceToString(item.cancelled_amount || 0) }} {{ getCurrencyCode(item.currency) }}</label>
          </template>
          <template v-slot:item.pay_mode="{ item }">
            <v-chip x-small :color="item.pg_id && item.pg_id.includes('test') ? 'orange' : 'blue'" dark>{{ item.pg_id && item.pg_id.includes('test') ? 'TEST' : '실결제' }}</v-chip>
          </template>
          <template v-slot:item.receipt="{ item }">
            <v-icon small @click="openReceipt(item)" :disabled="!item.receipt_url" :color="item.receipt_url ? 'blue' : 'grey'">mdi-receipt</v-icon>
          </template>

        </v-data-table>
      </v-col>
    </v-row>

    <!-- 페이징 -->
    <v-row justify="center" align="center" class="mt-2">
      <v-btn small outlined :disabled="prevPage == null" @click="loadpage(prevPage)" class="mr-2">
        <v-icon left small>mdi-chevron-left</v-icon>이전
      </v-btn>
      <span class="mx-3">{{ page }} 페이지</span>
      <v-btn small outlined :disabled="nextPage == null" @click="loadpage(nextPage)" class="ml-2">
        다음<v-icon right small>mdi-chevron-right</v-icon>
      </v-btn>
    </v-row>

  </v-container>
</template>

<script>
import moment from 'moment'

export default {

  data: () => ({
    // 업체/행사 검색
    SEARCH_COM_IDX: null,
    SEARCH_COM_NAME: '',
    SEARCH_EVENT_IDX: null,
    SEARCH_EVENT_NAME: '',

    // 날짜 검색
    date1: false,
    date2: false,
    search_start_date: '',
    search_end_date: '',
    search_status: 'all',
    search_keyword: '',

    // 상태 목록
    statusList: [
      { name: '전체', value: 'all' },
      { name: '결제완료', value: 'paid' },
      { name: '취소', value: 'cancelled' },
      { name: '실패', value: 'failed' }
    ],

    // 페이징
    page: 1,
    prevPage: null,
    nextPage: null,
    loading: false,

    // 테이블 헤더 (포트원 UI 순서)
    headers: [
      { text: '상세', value: 'detail', width: 100, sortable: false, align: 'center' },
      { text: '결제상태', value: 'status', width: 100, sortable: false, align: 'center' },
      { text: '상태승인시점', value: 'paid_at', width: 200, sortable: false, align: 'center' },
      { text: '고객사 거래번호', value: 'merchant_uid', width: 200, sortable: false, align: 'center' },
      { text: '결제대행사', value: 'pg_provider', width: 150, sortable: false, align: 'center' },
      { text: '주문명', value: 'name', width: 200, sortable: false, align: 'center' },
      { text: '주문자명', value: 'buyer_name', width: 100, sortable: false, align: 'center' },
      { text: '결제수단', value: 'pay_method', width: 100, sortable: false, align: 'center' },
      { text: '결제금액', value: 'amount', width: 150, sortable: false, align: 'right' },
      { text: '전체취소금액', value: 'cancel_amount', width: 150, sortable: false, align: 'right' },
      { text: '부분취소금액', value: 'cancelled_amount', width: 150, sortable: false, align: 'right' },
      { text: '결제모드', value: 'pay_mode', width: 100, sortable: false, align: 'center' },
      { text: '영수증', value: 'receipt', width: 100, sortable: false, align: 'center' }
    ],
    itemList: [],

    // 페이지 ROW
    pagerow: 1000,

    // 상세 정보 다이얼로그
    dialog_detail: false,
    detailData: {},

    grade: 0
  }),

  mounted() {
    if (this.$session.get("GRADE") < 10) {
      this.$alert("권한이 없습니다.");
      this.$router.push("Login");
      return;
    }

    this.grade = this.$session.get("GRADE");

    // 세션에서 업체/행사 정보 로드
    this.SEARCH_COM_IDX = this.$session.get("COM_IDX");
    this.SEARCH_COM_NAME = this.$session.get("COM_NAME");
    this.SEARCH_EVENT_IDX = this.$session.get("EVENT_IDX");
    this.SEARCH_EVENT_NAME = this.$session.get("EVENT_NAME");

    // 기본 날짜 설정 (최근 1개월)
    this.search_end_date = moment().format("YYYY-MM-DD");
    this.search_start_date = moment().subtract(1, 'months').format("YYYY-MM-DD");

    if (this.$session.exists("ADMIN_IDX")) {
      this.loadpage(1);
    } else {
      this.$router.push("Login");
    }
  },

  methods: {

    // 업체선택 콜백
    comsearchcb(val1) {
      if (val1.NAME.length > 0) {
        this.SEARCH_COM_NAME = val1.NAME;
        this.SEARCH_COM_IDX = val1.IDX;
        this.$session.set("COM_IDX", val1.IDX);
        this.$session.set("COM_NAME", val1.NAME);
        this.SEARCH_EVENT_IDX = null;
        this.SEARCH_EVENT_NAME = '';
        this.$session.set("EVENT_IDX", null);
        this.$session.set("EVENT_NAME", "");
      } else {
        this.SEARCH_COM_NAME = '';
        this.SEARCH_COM_IDX = null;
        this.SEARCH_EVENT_IDX = null;
        this.SEARCH_EVENT_NAME = '';
        this.$session.set("COM_IDX", null);
        this.$session.set("COM_NAME", "");
        this.$session.set("EVENT_IDX", null);
        this.$session.set("EVENT_NAME", "");
      }
      this.loadpage(1);
    },

    // 업체선택 취소 콜백
    comsearchclear() {
      this.SEARCH_COM_IDX = null;
      this.SEARCH_COM_NAME = '';
      this.SEARCH_EVENT_IDX = null;
      this.SEARCH_EVENT_NAME = '';
      this.$session.set("COM_IDX", null);
      this.$session.set("COM_NAME", "");
      this.$session.set("EVENT_IDX", null);
      this.$session.set("EVENT_NAME", "");
      this.loadpage(1);
    },

    // 행사선택 콜백
    eventsearchcb(val1) {
      if (val1.NAME.length > 0) {
        this.SEARCH_EVENT_NAME = val1.NAME;
        this.SEARCH_EVENT_IDX = val1.IDX;
        this.$session.set("EVENT_IDX", val1.IDX);
        this.$session.set("EVENT_NAME", val1.NAME);
      } else {
        this.SEARCH_EVENT_NAME = "";
        this.SEARCH_EVENT_IDX = null;
        this.$session.set("EVENT_IDX", null);
        this.$session.set("EVENT_NAME", "");
      }
      this.loadpage(1);
    },

    search_run() {
      this.loadpage(1);
    },

    loadpage(p) {

      if (this.SEARCH_EVENT_IDX == null || this.SEARCH_EVENT_IDX == undefined || this.SEARCH_EVENT_IDX == '') {
        this.$alert(this.$t('common_select_event'));
        return;
      }

      // 포트원 API는 조회 기간이 90일 미만이어야 함 (정확히 90일도 안됨)
      if (this.search_start_date && this.search_end_date) {
        const startMoment = moment(this.search_start_date);
        const endMoment = moment(this.search_end_date);
        const diffDays = endMoment.diff(startMoment, 'days');
        if (diffDays >= 90) {
          this.$alert(this.$t('common_select_90'));
          return;
        }
      }

      this.itemList.splice(0, this.itemList.length);
      this.loading = true;
      this.page = p;

      // 날짜를 unix timestamp로 변환
      const fromTimestamp = this.search_start_date ? moment(this.search_start_date).startOf('day').unix() : null;
      const toTimestamp = this.search_end_date ? moment(this.search_end_date).endOf('day').unix() : null;

      this.$http.post(this.$host + '/api/PortonePaymentList', {
        page: this.page,
        limit: this.pagerow,
        status: this.search_status,
        from: fromTimestamp,
        to: toTimestamp
      }, {
        headers: {
          accesstoken: this.$session.get("ACCESSTOKEN"),
          reflashtoken: this.$session.get("REFLASHTOKEN")
        }
      })
      .then((result) => {
        this.loading = false;

        if (result.data.resultCode == 0) {
          // 토큰 저장
          if (result.data.accesstoken != null && result.data.accesstoken.length > 0) {
            this.$session.set("ACCESSTOKEN", result.data.accesstoken);
          }
          if (result.data.reflashtoken != null && result.data.reflashtoken.length > 0) {
            this.$session.set("REFLASHTOKEN", result.data.reflashtoken);
          }

          let list = result.data.resultData;

          // 1. 행사명이 선택된 경우 상품명(name)에 행사명이 포함된 건만 필터링
          // 상품명이 잘릴 수 있으므로 행사명의 앞부분(40바이트)만 비교
          if (this.SEARCH_EVENT_NAME && this.SEARCH_EVENT_NAME.length > 0) {
            const truncatedEventName = this.getStringByByteLength(this.SEARCH_EVENT_NAME, 40);
            list = list.filter(item => item.name && item.name.includes(truncatedEventName));
          }

          // 2. 주문자명 필터링
          if (this.search_keyword && this.search_keyword.length > 0) {
            list = list.filter(item => {
              const matched = item.buyer_name && item.buyer_name.includes(this.search_keyword);
              return matched;
            });
          }

          // 페이징 정보 저장
          this.prevPage = result.data.previous;
          this.nextPage = result.data.next;

          list.forEach(element => {
            this.itemList.push(element);
          });

        } else if (result.data.resultCode == 2) {
          this.$alert("인증이 만료되었습니다.");
          this.$session.clear();
          this.$router.push('Login');
        } else {
          this.$alert(result.data.resultMsg || "조회 실패");
        }
      })
      .catch((error) => {
        this.loading = false;
        console.log(error);
        this.$alert("서버 통신 오류");
      });
    },


    showDetail(item) {
      this.detailData = item;
      this.dialog_detail = true;
    },

    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success('복사되었습니다.');
      }).catch(() => {
        this.$toast.error('복사 실패');
      });
    },

    getStatusTextColor(status) {
      const colorMap = {
        'ready': '#ff9800',
        'paid': '#4caf50',
        'cancelled': '#2196f3',
        'failed': '#9e9e9e'
      };
      return colorMap[status] || '#333';
    },

    openCancelReceipt() {
      if (this.detailData.cancel_receipt_urls && this.detailData.cancel_receipt_urls.length > 0) {
        window.open(this.detailData.cancel_receipt_urls[0], '_blank');
      }
    },

    openCancelReceiptUrl(url) {
      if (url) {
        window.open(url, '_blank');
      }
    },

    priceToString(price) {
      if (price == null || price == undefined) return '0';
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // 통화 코드 반환
    getCurrencyCode(currency) {
      if (currency === 'USD') return 'USD';
      return 'KRW';
    },

    formatDate(timestamp) {
      if (!timestamp) return '-';
      return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    },

    // 상태별 시간 반환
    getStatusTime(item) {
      switch(item.status) {
        case 'paid': return item.paid_at;
        case 'cancelled': return item.cancelled_at;
        case 'failed': return item.failed_at;
        case 'ready': return item.started_at;
        default: return item.paid_at || item.started_at;
      }
    },

    getStatusName(status) {
      const statusMap = {
        'ready': '결제대기',
        'paid': '결제완료',
        'cancelled': '취소',
        'failed': '실패'
      };
      return statusMap[status] || status;
    },

    getStatusColor(status) {
      const colorMap = {
        'ready': 'orange',
        'paid': 'green',
        'cancelled': 'red',
        'failed': 'grey'
      };
      return colorMap[status] || 'grey';
    },

    getPayMethodName(method) {
      const methodMap = {
        'card': '카드',
        'trans': '계좌이체',
        'vbank': '가상계좌',
        'phone': '휴대폰',
        'kakaopay': '카카오페이',
        'naverpay': '네이버페이',
        'samsung': '삼성페이',
        'payco': '페이코',
        'tosspay': '토스페이'
      };
      return methodMap[method] || method || '-';
    },

    getPgName(pg_provider) {
      const pgMap = {
        'html5_inicis': '이니시스',
        'inicis': '이니시스',
        'kcp': 'NHN KCP',
        'kcp_billing': 'NHN KCP',
        'uplus': '토스페이먼츠',
        'tosspayments': '토스페이먼츠',
        'nice': '나이스페이',
        'nice_v2': '나이스페이',
        'jtnet': 'JTNet',
        'kicc': '이지페이',
        'danal': '다날',
        'danal_tpay': '다날',
        'mobilians': '모빌리언스',
        'settle': '헥토파이낸셜',
        'settle_acc': '헥토파이낸셜',
        'payco': '페이코',
        'kakaopay': '카카오페이',
        'naverpay': '네이버페이',
        'smilepay': '스마일페이',
        'tosspay': '토스페이',
        'chai': '차이',
        'paypal': '페이팔',
        'eximbay': '엑심베이'
      };
      return pgMap[pg_provider] || pg_provider || '-';
    },

    // 바이트 기준으로 문자열 자르기 (한글 2바이트, 영문 1바이트)
    getStringByByteLength(str, maxBytes) {
      if (!str) return '';
      let byteCount = 0;
      let result = '';
      for (let i = 0; i < str.length; i++) {
        const charCode = str.charCodeAt(i);
        // 한글 및 기타 2바이트 문자 체크 (유니코드 범위)
        const charBytes = (charCode > 127) ? 2 : 1;
        if (byteCount + charBytes > maxBytes) break;
        byteCount += charBytes;
        result += str[i];
      }
      return result;
    },

    openReceipt(item) {
      if (!item.receipt_url) {
        this.$alert("영수증 URL이 없습니다.");
        return;
      }
      window.open(item.receipt_url, '_blank');
    }

  }

}
</script>

<style scoped>
.menunamestyle {
  font-size: 20px;
  font-weight: bold;
}
.total_label {
  font-size: 14px;
  margin-right: 5px;
}
.cheader_normal {
  font-size: 12px;
  font-weight: bold;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}
.info-table {
  background: transparent !important;
}
.info-table tbody tr {
  border-bottom: none !important;
}
.info-table tbody tr:hover {
  background: transparent !important;
}
.label-cell {
  width: 130px;
  color: #666;
  font-size: 13px;
  padding: 6px 0 !important;
  border-bottom: none !important;
  background: transparent !important;
}
.value-cell {
  color: #333;
  font-size: 13px;
  padding: 6px 0 !important;
  border-bottom: none !important;
  background: transparent !important;
}

</style>
